import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringApiService } from '../../../../core/services/catering-api.service';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { CateringVendorService } from '../../../../core/services/catering-vendor.service';
import { BookMeetingService } from '../../../../core/services/book-meeting.service';
import { DietaryPreferencesService } from '../../../../core/services/dietary-preferences.service';
import { LocaleService } from '../../../../core/services/locale.service';
import { LocationStateService } from '../../../../core/services/location-state.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { BookMeetingView, isVendorType, VendorType } from '../../../../core/constants/enums';
import { CateringMenuItem } from '../../../../core/models/catering.model';
import { formatCurrencyMinor, formatHHMMInTimezone, isVendorOpen, DIETARY_LABELS } from '../../../../core/utils/catering';
import { UK_ALLERGENS } from '../../../../core/constants/dietary';
import { DietaryBadgesComponent } from '../dietary-badges/dietary-badges.component';
import { AllergenBadgesComponent } from '../allergen-badges/allergen-badges.component';
import { CartBarComponent } from '../cart-bar/cart-bar.component';
import { ItemCustomiseDialogComponent } from '../item-customise-dialog/item-customise-dialog.component';
import { DietaryPreferencesSetupDialogComponent } from '../dietary-preferences-setup-dialog/dietary-preferences-setup-dialog.component';
import {
  PaperIconComponent,
  PaperButtonComponent,
  PaperCardComponent,
  PaperPageTabsComponent,
  PageTab,
} from '@ricoh-paper/angular';

/**
 * MenuBrowseComponent — premium UberEats/Deliveroo-inspired menu browsing.
 *
 * Layout: vendor hero → sticky tabs/search/dietary → featured items → category sections.
 * Items use a horizontal card layout (text left, image right).
 */
@Component({
  selector: 'ct-menu-browse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DietaryBadgesComponent,
    AllergenBadgesComponent,
    CartBarComponent,
    ItemCustomiseDialogComponent,
    DietaryPreferencesSetupDialogComponent,
    PaperIconComponent,
    PaperButtonComponent,
    PaperCardComponent,
    PaperPageTabsComponent,
    TranslocoPipe,
  ],
  template: `
    <div class="mb">

      <!-- ══ Vendor Hero ══════════════════════════ -->
      @if (vendor(); as v) {
        <div class="mb__hero">
          @if (v.coverImage || v.logo) {
            <img class="mb__hero-img" [src]="v.coverImage || v.logo" alt=""
                 loading="lazy" (error)="$any($event.target).style.display='none'" />
          }
          <div class="mb__hero-gradient"></div>
          <div class="mb__hero-content">
            @if (v.logo) {
              <img class="mb__hero-logo" [src]="v.logo" [alt]="v.name"
                   (error)="$any($event.target).style.display='none'" />
            } @else {
              <span class="mb__hero-logo mb__hero-logo--letter">{{ v.name.charAt(0) }}</span>
            }
            <div class="mb__hero-info">
              <h2 class="mb__hero-name">{{ v.name }}</h2>
              <div class="mb__hero-meta">
                @if (v.ratings; as r) {
                  <span class="mb__hero-rating">&#9733; {{ r.average.toFixed(1) }} <span class="mb__hero-count">({{ r.count }})</span></span>
                }
                @if (v.estimatedDeliveryMinutes) {
                  <span class="mb__hero-sep">&middot;</span>
                  <span class="mb__hero-eta">{{ heroEtaLabel(v.estimatedDeliveryMinutes) }}</span>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <!-- ══ Sticky Header ════════════════════════ -->
      <div class="mb__sticky">
        <!-- Category tabs -->
        <div class="mb__tabs">
          <paper-page-tabs
            [tabs]="categoryTabs()"
            [active]="activeCategoryTab()"
            variant="underline"
            [ariaLabel]="'catering.customer.menu_browse.tabs_aria' | transloco"
            (activeChange)="onCategoryTabChange($event)" />
        </div>

        <!-- Search + Edit Preferences -->
        <div class="mb__search-row">
          <div class="mb__search-wrap">
            <paper-icon class="mb__search-icon" name="search" [size]="14" aria-hidden="true" />
            <input type="text" class="mb__search" [placeholder]="searchPlaceholder()"
                   [value]="searchQuery()"
                   (input)="searchQuery.set($any($event.target).value)" />
          </div>
          <paper-button
            class="mb__pref-btn"
            intent="secondary"
            size="xs"
            icon="settings"
            [ariaLabel]="'catering.customer.menu_browse.edit_preferences' | transloco"
            (click)="openPreferencesDialog()"
            [title]="'catering.customer.menu_browse.edit_preferences' | transloco">
            <span class="mb__pref-btn-text">{{ 'catering.customer.menu_browse.edit_preferences' | transloco }}</span>
          </paper-button>
        </div>

        <!-- Dietary filters -->
        <div class="mb__dietary">
          @for (d of dietaryOptions(); track d.key) {
            <paper-button
              class="mb__diet-chip"
              shape="pill"
              size="xs"
              [class.active]="isDietaryActive(d.key)"
              [intent]="isDietaryActive(d.key) ? 'primary' : 'secondary'"
              [ariaPressed]="isDietaryActive(d.key)"
              (click)="toggleDietary(d.key)">
              {{ d.label }}
            </paper-button>
          }
        </div>

        <!-- Allergen filters -->
        @if (allergenFilters().length > 0 || activeFilterCount() > 0) {
          <div class="mb__allergens">
            <span class="mb__filter-label">{{ 'catering.customer.menu_browse.exclude_allergens' | transloco }}</span>
            @for (a of allergens; track a) {
              <paper-button
                class="mb__allergen-chip"
                shape="pill"
                size="xs"
                intent="secondary"
                [class.active]="allergenFilters().includes(a)"
                [ariaPressed]="allergenFilters().includes(a)"
                (click)="toggleAllergen(a)">
                {{ a }}
              </paper-button>
            }
          </div>
        }

        <!-- Filter summary + Clear all -->
        @if (activeFilterCount() > 0) {
          <div class="mb__filter-summary">
            <span class="mb__summary-text">{{ filterSummary() }}</span>
            <paper-button class="mb__clear-all" intent="ghost" size="xs" (click)="clearAllFilters()">{{ 'catering.customer.menu_browse.clear_all' | transloco }}</paper-button>
          </div>
        }
      </div>

      <!-- ══ Content ══════════════════════════════ -->
      @if (loading()) {
        <div class="mb__loading">
          <div class="mb__loading-pulse"></div>
          <div class="mb__loading-pulse mb__loading-pulse--short"></div>
          <div class="mb__loading-pulse"></div>
        </div>
      } @else if (isEmailOnly()) {
        <!-- Email-only vendor — show request prompt instead of menu -->
        <div class="mb__email-only">
          <div class="mb__email-icon">
            <paper-icon name="email" [size]="32" aria-hidden="true" />
          </div>
          <h4 class="mb__email-title">{{ 'catering.customer.menu_browse.email_only.title' | transloco }}</h4>
          <p class="mb__email-desc">{{ emailOnlyDesc() }}</p>
          @if (vendor()?.description) {
            <p class="mb__email-vendor-desc">"{{ vendor()?.description }}"</p>
          }
          <paper-button class="mb__email-btn" intent="primary" size="md" (click)="switchToEmailRequest()">
            {{ 'catering.customer.menu_browse.email_only.send_request' | transloco }}
          </paper-button>
        </div>
      } @else {

        <!-- Featured items (horizontal scroll) -->
        @if (featuredItems().length > 0 && !searchQuery() && !selectedCategory()) {
          <div class="mb__featured">
            <h3 class="mb__section-title">{{ 'catering.customer.menu_browse.featured' | transloco }}</h3>
            <div class="mb__featured-scroll">
              @for (item of featuredItems(); track item._id) {
                <div class="mb__featured-card-shell">
                  <paper-card [interactive]="true" (clicked)="quickAdd(item)">
                    @if (item.image) {
                      <img class="mb__featured-img" [src]="item.image" [alt]="item.name" loading="lazy"
                           (error)="$any($event.target).style.display='none'" />
                    } @else {
                      <div class="mb__featured-img mb__featured-img--placeholder"></div>
                    }
                    <span class="mb__featured-name">{{ item.translatedName || item.name }}</span>
                    <span class="mb__featured-price">
                      @if (item.costPence; as cost) {
                        {{ fmtPrice(cost) }}
                      }
                      <span class="mb__featured-add">+</span>
                    </span>
                  </paper-card>
                </div>
              }
            </div>
          </div>
        }

        <!-- Category sections -->
        @for (section of categorySections(); track section.category) {
          <div class="mb__category-section">
            <h3 class="mb__section-title">{{ section.category }}</h3>
            @for (item of section.items; track item._id) {
              <!-- Item card: text left, image right -->
              <div class="mb__item" [class.unavailable]="!isItemAvailable(item)">
                <div class="mb__item-body">
                  <span class="mb__item-name">{{ item.translatedName || item.name }}</span>
                  @if (item.description) {
                    <span class="mb__item-desc">{{ item.translatedDescription || item.description }}</span>
                  }
                  <div class="mb__item-price-row">
                    @if (item.costPence; as cost) {
                      <span class="mb__item-price">{{ fmtPrice(cost) }}</span>
                    }
                    @if (item.dietaryTags.length) {
                      <ct-dietary-badges [tags]="item.dietaryTags" />
                    }
                    @if (item.allergens; as allergens) {
                      @if (allergens.length) {
                        <ct-allergen-badges [allergens]="allergens" [maxVisible]="3" />
                      }
                    }
                  </div>
                  <!-- Quantity control -->
                  @if (isItemAvailable(item)) {
                    <div class="mb__item-qty">
                      @if (getCartQuantity(item._id) > 0) {
                        <paper-button
                          class="mb__qty-btn"
                          intent="secondary"
                          size="sm"
                          shape="icon"
                          icon="minus"
                          [ariaLabel]="'catering.customer.menu_browse.qty.decrease' | transloco"
                          (click)="changeQty(item, -1); $event.stopPropagation()" />
                        <span class="mb__qty-num">{{ getCartQuantity(item._id) }}</span>
                      }
                      <paper-button
                        class="mb__qty-btn mb__qty-btn--add"
                        intent="secondary"
                        size="sm"
                        shape="icon"
                        icon="plus"
                        [ariaLabel]="'catering.customer.menu_browse.qty.increase' | transloco"
                        (click)="changeQty(item, 1); $event.stopPropagation()"
                        [disabled]="item.maxQuantity ? getCartQuantity(item._id) >= item.maxQuantity : false" />
                    </div>
                  } @else {
                    <span class="mb__item-unavail">
                      @if (item.availableUntil) { {{ availableUntilLabel(item.availableUntil) }} }
                      @else if (item.availableFrom) { {{ availableFromLabel(item.availableFrom) }} }
                    </span>
                  }
                </div>
                @if (item.image) {
                  <div class="mb__item-img-wrap">
                    <img class="mb__item-img" [src]="item.image" [alt]="item.name" loading="lazy"
                         (error)="$any($event.target).style.display='none'" />
                  </div>
                }
              </div>
            }
          </div>
        }

        @if (filteredItems().length === 0) {
          @if (isEmailOnly()) {
            <div class="mb__email-only">
              <div class="mb__email-icon">
                <paper-icon name="email" [size]="32" aria-hidden="true" />
              </div>
              <h4 class="mb__email-title">{{ 'catering.customer.menu_browse.email_only.title' | transloco }}</h4>
              <p class="mb__email-desc">{{ emailOnlyDescAlt() }}</p>
              <paper-button class="mb__email-btn" intent="primary" size="md" (click)="switchToEmailRequest()">
                {{ 'catering.customer.menu_browse.email_only.send_request' | transloco }}
              </paper-button>
            </div>
          } @else {
            <div class="mb__empty">
              <paper-icon class="mb__empty-icon" name="search" [size]="40" aria-hidden="true" />
              <h4 class="mb__empty-title">{{ 'catering.customer.menu_browse.empty.title' | transloco }}</h4>
              <p class="mb__empty-desc">
                @if (searchQuery()) {
                  {{ noResultsLabel() }}
                } @else {
                  {{ 'catering.customer.menu_browse.empty.adjust_filters' | transloco }}
                }
              </p>
              @if (activeFilterCount() > 0) {
                <paper-button class="mb__empty-action" intent="secondary" size="sm" (click)="clearAllFilters()">
                  {{ 'catering.customer.menu_browse.empty.clear_all_filters' | transloco }}
                </paper-button>
              }
            </div>
          }
        }
      }

      <!-- Sticky cart bar -->
      <ct-cart-bar (viewCart)="onViewCart()" />

      <!-- Customise dialog (overlay) -->
      @if (customisingItem(); as cItem) {
        <ct-item-customise
          [item]="cItem"
          (confirmed)="onCustomiseConfirmed($event)"
          (cancelled)="customisingItem.set(null)" />
      }

      <!-- Dietary preferences setup dialog (overlay) -->
      @if (showSetupDialog()) {
        <ct-dietary-preferences-setup
          (preferencesSaved)="onPreferencesSaved($event)"
          (cancelled)="onSetupCancelled()" />
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .mb {
      padding-bottom: 64px; /* space for cart bar */
    }

    /* ═══ Vendor Hero ═══════════════════════ */

    .mb__hero {
      position: relative;
      height: 180px;
      background: var(--surface-3);
      overflow: hidden;
      margin: -20px -20px 0; /* bleed to panel edges */
    }

    .mb__hero-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .mb__hero-gradient {
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, transparent 30%, rgba(0, 0, 0, 0.7) 100%);
      pointer-events: none;
    }

    .mb__hero-content {
      position: absolute;
      bottom: 14px;
      left: 16px;
      right: 16px;
      display: flex;
      align-items: flex-end;
      gap: 12px;
    }

    .mb__hero-logo {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      border: 2px solid rgba(255, 255, 255, 0.2);
      background: #fff;
      object-fit: contain;
      padding: 4px;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

      &--letter {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        font-size: 20px;
        font-weight: 700;
        color: var(--text-on-accent, #fff);
        background: var(--color-primary);
      }
    }

    .mb__hero-info { flex: 1; min-width: 0; }

    .mb__hero-name {
      font-size: 20px;
      font-weight: 700;
      color: #fff;
      margin: 0;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mb__hero-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 2px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.85);
    }

    .mb__hero-rating { color: #fbbf24; font-weight: 600; }
    .mb__hero-count { color: rgba(255, 255, 255, 0.6); font-weight: 400; }
    .mb__hero-sep { color: rgba(255, 255, 255, 0.4); }
    .mb__hero-eta { color: rgba(255, 255, 255, 0.7); }

    /* ═══ Sticky Header ════════════════════ */

    .mb__sticky {
      position: sticky;
      top: -20px; /* compensate for parent padding so it sits flush against panel header */
      z-index: 10;
      background: var(--card-bg-solid);
      margin: 0 -20px;
      padding: 12px 20px 8px;
      border-bottom: 1px solid var(--border-low);
    }

    .mb__tabs {
      display: flex;
      overflow-x: auto;
      scrollbar-width: none;
      margin-bottom: 8px;
      &::-webkit-scrollbar { display: none; }
    }

    .mb__search-row {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      align-items: center;
    }

    .mb__search-wrap {
      position: relative;
      flex: 1;
    }

    .mb__search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-tertiary);
      pointer-events: none;
    }

    .mb__search {
      width: 100%;
      padding: 8px 10px 8px 30px;
      font-size: 12px;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      transition: border-color 0.15s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--color-primary); }
    }

    .mb__pref-btn {
      flex-shrink: 0;
    }

    @media (max-width: 480px) {
      .mb__pref-btn {
        ::ng-deep .paper-button__label {
          display: none;
        }
      }
    }

    .mb__dietary {
      display: flex;
      gap: 5px;
      flex-wrap: wrap;
      margin-bottom: 6px;
    }

    .mb__diet-chip {
      --im-button-font-weight: 600;
      --surface-raised: var(--surface-2);
      --text-primary: var(--text-secondary);
      --border-default: var(--border-low);
      --surface-hover: var(--surface-3);
      --border-strong: var(--border-med);
      --im-button-min-height: 24px;
      --im-button-padding-x: 10px;
      --im-button-font-size: var(--fs-tag);

      &:not(.active):hover {
        --text-primary: var(--text);
      }
    }

    .mb__allergens {
      display: flex;
      gap: 5px;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 6px;
      overflow-x: auto;
      scrollbar-width: none;
      &::-webkit-scrollbar { display: none; }
    }

    .mb__filter-label {
      font-size: 10px;
      font-weight: 600;
      color: var(--text-tertiary);
      flex-shrink: 0;
      margin-right: 2px;
    }

    .mb__allergen-chip {
      white-space: nowrap;
      flex-shrink: 0;
      --im-button-font-weight: 600;
      --surface-raised: var(--surface-2);
      --text-primary: var(--text-secondary);
      --border-default: var(--border-low);
      --surface-hover: var(--surface-3);
      --border-strong: var(--border-med);
      --im-button-min-height: 24px;
      --im-button-padding-x: 10px;
      --im-button-font-size: var(--fs-tag);

      &:not(.active):hover {
        --text-primary: var(--text);
      }

      &.active {
        --text-primary: var(--danger);
        --surface-raised: color-mix(in srgb, var(--danger) 15%, transparent);
        --border-default: var(--danger);
        --surface-hover: color-mix(in srgb, var(--danger) 18%, transparent);
        --border-strong: var(--danger);
      }
    }

    .mb__filter-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding-top: 6px;
      border-top: 1px solid var(--border-low);
    }

    .mb__summary-text {
      font-size: 11px;
      color: var(--text-tertiary);
      font-weight: 500;
    }

    .mb__clear-all {
      white-space: nowrap;
      --text-secondary: var(--color-primary);
      --text-primary: var(--color-primary);
      --im-button-font-weight: 600;

      &:hover {
        ::ng-deep .paper-button__label {
          text-decoration: underline;
        }
      }
    }

    /* ═══ Loading Skeleton ═════════════════ */

    .mb__loading {
      padding: 20px 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .mb__loading-pulse {
      height: 80px;
      background: var(--surface-2);
      border-radius: var(--radius);
      animation: pulse 1.5s ease-in-out infinite;

      &--short { width: 66%; }
    }

    @keyframes pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }

    /* ═══ Featured Items ═══════════════════ */

    .mb__featured {
      padding: 16px 0 8px;
    }

    .mb__section-title {
      font-size: 16px; /* design flow-menu: category headings at 16px */
      font-weight: 700;
      color: var(--text);
      margin: 0 0 10px;
    }

    .mb__featured-scroll {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      scrollbar-width: none;
      padding: 4px;
      &::-webkit-scrollbar { display: none; }
    }

    .mb__featured-card-shell {
      flex-shrink: 0;
      width: 150px;
      transition: transform var(--motion) var(--ease);

      &:hover {
        transform: translateY(-1px);
      }

      paper-card {
        --surface-raised: var(--surface-2);
        --border-subtle: var(--border-low);
        --im-card-hover-shadow: none;
        --im-card-focus-shadow: none;
      }
    }

    .mb__featured-img {
      width: 100%;
      aspect-ratio: 4 / 3;
      object-fit: cover;
      display: block;

      &--placeholder {
        background: linear-gradient(135deg, var(--surface-3) 0%, var(--surface-2) 100%);
      }
    }

    .mb__featured-name {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 8px 8px 2px;
      font-size: 12px;
      font-weight: 500;
      color: var(--text);
      line-height: 1.3;
      min-height: 34px; /* space for 2 lines */
    }

    .mb__featured-price {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px 10px;
      font-size: 12px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
      min-height: 30px;
    }

    .mb__featured-add {
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      line-height: 1;
      color: var(--color-primary);
      background: var(--surface-2);
      border: 1px solid var(--color-primary);
      border-radius: 50%;
      flex-shrink: 0;
      margin-left: auto;
      /* Slightly nudge the glyph upward so the optical centre matches the
         circle. The "+" character has more visual weight below the baseline
         than above, which makes a strict centre look low. */
      padding-bottom: 1px;
    }

    /* ═══ Category Sections ════════════════ */

    .mb__category-section {
      padding: 16px 0 0;
    }

    /* ═══ Item Cards ═══════════════════════ */

    .mb__item {
      display: flex;
      gap: 12px;
      padding: 14px 0;
      border-bottom: 1px solid var(--border-low);
      cursor: default;

      &.unavailable { opacity: 0.45; }

      &:last-child { border-bottom: none; }
    }

    .mb__item-body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .mb__item-name {
      font-size: 14px;
      font-weight: 500;
      color: var(--text);
      line-height: 1.3;
    }

    .mb__item-desc {
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.35;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .mb__item-price-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 4px;
    }

    .mb__item-price {
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    .mb__item-qty {
      display: flex;
      align-items: center;
      gap: 0;
      margin-top: 6px;
    }

    .mb__qty-btn {
      --radius-sm: var(--radius-pill);

      &--add {
        --text-primary: var(--color-primary);
        --border-default: var(--color-primary);
        --surface-hover: var(--color-primary-bg);
        --border-strong: var(--color-primary);
      }
    }

    .mb__qty-num {
      width: 28px;
      text-align: center;
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    .mb__item-unavail {
      font-size: 10px;
      color: var(--text-tertiary);
      margin-top: 4px;
    }

    .mb__item-img-wrap {
      flex-shrink: 0;
      width: 100px;
      height: 100px;
      border-radius: 10px;
      overflow: hidden;
      background: var(--surface-3);
    }

    .mb__item-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    /* ═══ Empty State ═════════════════════ */

    .mb__empty {
      text-align: center;
      padding: 60px 20px;
    }

    .mb__empty-icon {
      display: block;
      margin: 0 auto 14px;
      color: var(--text-tertiary);
      opacity: 0.5;
    }

    .mb__empty-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--text);
      margin: 0 0 6px;
    }

    .mb__empty-desc {
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.5;
      margin: 0 0 20px;
      max-width: 300px;
      margin-left: auto;
      margin-right: auto;
    }

    .mb__empty-action {
      --surface-raised: color-mix(in srgb, var(--color-primary) 10%, transparent);
      --text-primary: var(--color-primary);
      --border-default: var(--color-primary);
      --surface-hover: color-mix(in srgb, var(--color-primary) 15%, transparent);
      --border-strong: var(--color-primary);
      --im-button-font-weight: 600;
    }

    /* ═══ Email-Only State ═════════════════ */

    .mb__email-only {
      text-align: center;
      padding: 40px 20px;
    }

    .mb__email-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--surface-3);
      color: var(--text-secondary);
      margin: 0 auto 14px;
    }

    .mb__email-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--text);
      margin: 0 0 8px;
    }

    .mb__email-desc {
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.5;
      margin: 0 0 20px;
      max-width: 280px;
      margin-left: auto;
      margin-right: auto;
    }

    .mb__email-btn {
      --im-button-font-weight: 600;

      &:hover {
        ::ng-deep .paper-button {
          filter: brightness(1.1);
        }
      }
    }

    .mb__email-vendor-desc {
      font-size: 12px;
      color: var(--text-tertiary);
      font-style: italic;
      line-height: 1.5;
      margin: 0 auto 20px;
      max-width: 280px;
    }

    /* ═══ Reduced Motion ══════════════════ */

    @media (prefers-reduced-motion: reduce) {
      .mb__search,
      .mb__featured-card-shell {
        transition: none;
      }
      .mb__loading-pulse { animation: none; opacity: 0.5; }
    }
  `],
})
export class MenuBrowseComponent {
  private readonly api = inject(CateringApiService);
  protected readonly vendorService = inject(CateringVendorService);
  protected readonly cartService = inject(CateringCartService);
  private readonly meetingService = inject(BookMeetingService);
  private readonly dietaryPrefsService = inject(DietaryPreferencesService);
  private readonly locationState = inject(LocationStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);
  private readonly analytics = inject(AnalyticsService);

  /** Vendor id we've most recently fired CATERING_VENDOR_BROWSED for —
   *  prevents re-firing when the vendor effect re-runs for unrelated
   *  signal changes (preferences, location, etc.). */
  private _lastBrowsedVendorId: string | null = null;

  protected heroEtaLabel(minutes: number): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.menu_browse.eta_delivery', { minutes });
  }
  protected readonly searchPlaceholder = computed(() => {
    this.locale.locale();
    const name = this.vendorService.selectedVendor()?.name || this.transloco.translate('catering.customer.menu_browse.menu_fallback');
    return this.transloco.translate('catering.customer.menu_browse.search_placeholder', { name });
  });
  protected readonly emailOnlyDesc = computed(() => {
    this.locale.locale();
    const v = this.vendorService.selectedVendor();
    return this.transloco.translate('catering.customer.menu_browse.email_only.desc', {
      name: v?.name || '',
      minutes: v?.estimatedDeliveryMinutes || 20,
    });
  });
  protected readonly emailOnlyDescAlt = computed(() => {
    this.locale.locale();
    const v = this.vendorService.selectedVendor();
    return this.transloco.translate('catering.customer.menu_browse.email_only.desc_alt', {
      name: v?.name || '',
      minutes: v?.estimatedDeliveryMinutes || 20,
    });
  });
  protected readonly noResultsLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('catering.customer.menu_browse.empty.no_results', { query: this.searchQuery() });
  });
  protected availableUntilLabel(time: string): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.menu_browse.available_until', { time });
  }
  protected availableFromLabel(time: string): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.menu_browse.available_from', { time });
  }

  // ── Local state ────────────────────────────────────

  private readonly _menuItems = signal<CateringMenuItem[]>([]);
  private readonly _menuId = signal<string | null>(null);
  private readonly _menuCurrency = signal<string>('GBP');
  protected readonly loading = signal(false);
  protected readonly searchQuery = signal('');
  protected readonly selectedCategory = signal<string | null>(null);
  protected readonly dietaryFilters = signal<string[]>([]);
  protected readonly allergenFilters = signal<string[]>([]);
  protected readonly showSetupDialog = signal(false);

  /** Format minor units using the active menu's currency (falls back to GBP). */
  protected fmtPrice(pence: number): string {
    return formatCurrencyMinor(pence, this._menuCurrency());
  }

  /** Item currently being customised in the dialog (null = dialog closed) */
  protected readonly customisingItem = signal<CateringMenuItem | null>(null);

  /** Abort controller for in-flight loadMenu calls — prevents stale data on rapid vendor switches. */
  private _loadAbort: AbortController | null = null;

  readonly dietaryOptions = computed(() => {
    this.locale.locale();
    const t = (k: string) => this.transloco.translate(k);
    return [
      { key: 'vegetarian', label: t('catering.customer.dietary.vegetarian') },
      { key: 'vegan', label: t('catering.customer.dietary.vegan') },
      { key: 'gluten_free', label: t('catering.customer.dietary.gluten_free') },
      { key: 'dairy_free', label: t('catering.customer.dietary.dairy_free') },
      { key: 'halal', label: t('catering.customer.dietary.halal') },
    ];
  });

  readonly allergens = UK_ALLERGENS;

  // ── Computed ───────────────────────────────────────

  protected readonly vendor = computed(() => {
    return this.vendorService.selectedVendor();
  });

  protected readonly isEmailOnly = computed(() => {
    const v = this.vendor();
    if (!v) return false;
    return isVendorType(v.vendorType, VendorType.EMAIL_ONLY);
  });

  protected readonly categories = computed(() => {
    const cats = this._menuItems()
      .map(i => i.category)
      .filter((c): c is string => !!c);
    return Array.from(new Set(cats));
  });

  protected readonly categoryTabs = computed<readonly PageTab[]>(() => {
    this.locale.locale();
    return [
      { id: '__all__', label: this.transloco.translate('catering.customer.menu_browse.category.all') },
      ...this.categories().map(category => ({ id: category, label: category })),
    ];
  });

  protected readonly activeCategoryTab = computed(() => this.selectedCategory() ?? '__all__');

  protected readonly featuredItems = computed(() =>
    this._menuItems().filter(i => i.isFeatured && i.enabled),
  );

  protected readonly filteredItems = computed(() => {
    let items = this._menuItems().filter(i => i.enabled !== false);

    // Category filter (EXISTING)
    const cat = this.selectedCategory();
    if (cat) items = items.filter(i => i.category === cat);

    // Multi-field search (ENHANCED: now includes ingredients)
    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      items = items.filter(i =>
        (i.translatedName || i.name).toLowerCase().includes(query) ||
        (i.translatedDescription || i.description || '').toLowerCase().includes(query) ||
        (i.ingredients || []).some(ing => ing.toLowerCase().includes(query)),
      );
    }

    // Dietary tags filter (EXISTING)
    const dietary = this.dietaryFilters();
    if (dietary.length > 0) {
      items = items.filter(i => dietary.every(tag => i.dietaryTags?.includes(tag)));
    }

    // Allergen exclusions filter (NEW)
    const allergens = this.allergenFilters();
    if (allergens.length > 0) {
      items = items.filter(i => !i.allergens?.some(a => allergens.includes(a.id)));
    }

    return items;
  });

  /** Items grouped by category for section rendering */
  protected readonly categorySections = computed(() => {
    const items = this.filteredItems();
    const sections: { category: string; items: CateringMenuItem[] }[] = [];
    const catMap = new Map<string, CateringMenuItem[]>();

    for (const item of items) {
      const cat = item.category || 'Other';
      let bucket = catMap.get(cat);
      if (!bucket) {
        bucket = [];
        catMap.set(cat, bucket);
      }
      bucket.push(item);
    }

    for (const [category, categoryItems] of catMap) {
      sections.push({ category, items: categoryItems });
    }

    return sections;
  });

  /** Count of active filters (dietary + allergen + category + search) */
  protected readonly activeFilterCount = computed(() =>
    this.dietaryFilters().length +
    this.allergenFilters().length +
    (this.selectedCategory() ? 1 : 0) +
    (this.searchQuery() ? 1 : 0)
  );

  /** Filter summary text for display */
  protected readonly filterSummary = computed(() => {
    this.locale.locale();
    const count = this.filteredItems().length;
    const filters: string[] = [];
    const dopts = this.dietaryOptions();

    if (this.searchQuery()) filters.push(`"${this.searchQuery()}"`);
    filters.push(...this.dietaryFilters().map(k =>
      dopts.find(d => d.key === k)?.label || k
    ));
    filters.push(...this.allergenFilters());

    const itemsLabel = this.transloco.translate('catering.customer.menu_browse.items_count', { count });
    if (filters.length === 0) return itemsLabel;
    return `${itemsLabel} • ${filters.join(', ')}`;
  });

  protected onCategoryTabChange(tabId: string): void {
    this.selectedCategory.set(tabId === '__all__' ? null : tabId);
  }

  // ── Effect: load menu when vendor changes ──────────

  private readonly vendorEffect = effect(() => {
    const vendor = this.vendorService.selectedVendor();
    if (vendor) {
      this.loadMenu(vendor._id);
      if (vendor._id !== this._lastBrowsedVendorId) {
        this._lastBrowsedVendorId = vendor._id;
        // Source heuristic — BMP open implies the user landed here from
        // the meeting flow. Without it, we treat it as a standalone visit
        // (home widget vs direct route is indistinguishable from here;
        // would need an explicit input to split further).
        const source = this.meetingService.visible() ? 'bmp' : 'standalone';
        this.analytics.capture(ANALYTICS_EVENTS.CATERING_VENDOR_BROWSED, {
          vendor_id: vendor._id,
          vendor_name: vendor.name,
          location_id: this.locationState.selectedLocationId() ?? undefined,
          source,
        });
      }
    } else {
      this._menuItems.set([]);
      this._menuId.set(null);
      this._lastBrowsedVendorId = null;
    }
  });

  // ── Effect: auto-apply user preferences when vendor changes ──────────

  private readonly applyPrefsEffect = effect(() => {
    const vendor = this.vendorService.selectedVendor();
    const prefs = this.dietaryPrefsService.preferences();

    if (!vendor || !prefs.autoApplyFilters) return;

    // Auto-set dietary filters from user preferences
    if (prefs.dietaryTags.length > 0) {
      this.dietaryFilters.set(prefs.dietaryTags);
    }

    // Auto-set allergen exclusions from user preferences
    if (prefs.allergenExclusions.length > 0) {
      this.allergenFilters.set(prefs.allergenExclusions);
    }
  }, { allowSignalWrites: true });

  // ── Methods ────────────────────────────────────────

  private async loadMenu(vendorId: string): Promise<void> {
    // Abort any in-flight request to prevent stale data from overwriting fresh data
    this._loadAbort?.abort();
    this._loadAbort = new AbortController();
    const abortSignal = this._loadAbort.signal;

    this.loading.set(true);
    try {
      // Pass the location's IANA timezone so availability windows are
      // evaluated against the event's wall-clock, not server UTC.
      const tz = this.locationState.timezone();
      const deliveryAt = this.cartService.deliveryTime() ?? undefined;
      // And the site, so each item's tax rate is the one this order will be
      // charged there — the cart totals from it.
      const menus = await this.api.loadMenu(
        vendorId,
        undefined,
        undefined,
        deliveryAt,
        undefined,
        tz,
        this.locationState.selectedLocationId() ?? undefined,
      );

      // If this request was superseded by a newer one, discard the result
      if (abortSignal.aborted) return;

      if (menus.length > 0) {
        this._menuId.set(menus[0]._id);
        // TELL THE CART, not just this component. `_menuCurrency` is private
        // and only formats the prices ON THIS PAGE, so the cart bar and the
        // cart summary went on rendering the cart's own currency — which
        // nothing ever set away from its 'GBP' default. A EUR menu therefore
        // priced every item in euros and totalled them in pounds.
        this._menuCurrency.set(menus[0].currency || 'GBP');
        this.cartService.setCurrency(menus[0].currency || 'GBP');
        const allItems = menus.flatMap(m =>
          m.items.map(item => ({ ...item, category: item.category ?? m.name })),
        );
        this._menuItems.set(allItems);
      } else {
        this._menuItems.set([]);
        this._menuId.set(null);
        this._menuCurrency.set('GBP');
      }
    } catch (err) {
      // If aborted, a newer request is handling state — don't overwrite
      if (abortSignal.aborted) return;
      console.warn('[MenuBrowse] loadMenu error:', err);
      this._menuItems.set([]);
    } finally {
      // Only clear loading if this request is still the active one
      if (!abortSignal.aborted) {
        this.loading.set(false);
      }
    }
  }

  protected isItemAvailable(item: CateringMenuItem): boolean {
    if (!item.availableFrom && !item.availableUntil) return true;
    const deliveryTime = this.cartService.deliveryTime();
    if (!deliveryTime) return true;
    // Format the delivery instant in the location's IANA timezone so
    // "available 09:00–11:00" compares against the event's wall-clock.
    const tz = this.locationState.timezone();
    const timeStr = formatHHMMInTimezone(deliveryTime, tz);
    if (item.availableFrom && timeStr < item.availableFrom) return false;
    if (item.availableUntil && timeStr > item.availableUntil) return false;
    return true;
  }

  protected isDietaryActive(key: string): boolean {
    return this.dietaryFilters().includes(key);
  }

  protected toggleDietary(key: string): void {
    this.dietaryFilters.update(f => f.includes(key) ? f.filter(x => x !== key) : [...f, key]);
  }

  protected toggleAllergen(allergen: string): void {
    this.allergenFilters.update(f => f.includes(allergen) ? f.filter(x => x !== allergen) : [...f, allergen]);
  }

  protected clearAllFilters(): void {
    this.searchQuery.set('');
    this.selectedCategory.set(null);
    this.dietaryFilters.set([]);
    this.allergenFilters.set([]);
  }

  protected openPreferencesDialog(): void {
    this.showSetupDialog.set(true);
  }

  protected onPreferencesSaved(event: { dietaryTags: string[]; allergens: string[] }): void {
    this.dietaryPrefsService.update({
      dietaryTags: event.dietaryTags,
      allergenExclusions: event.allergens,
    });
    this.showSetupDialog.set(false);

    // Apply the new preferences immediately
    this.dietaryFilters.set(event.dietaryTags);
    this.allergenFilters.set(event.allergens);
  }

  protected onSetupCancelled(): void {
    this.showSetupDialog.set(false);
  }

  protected getCartQuantity(menuItemId: string): number {
    return this.cartService.getTotalQuantityForItem(menuItemId);
  }

  protected quickAdd(item: CateringMenuItem): void {
    if (item.questions?.length) {
      // Open customise dialog for items with options
      this.customisingItem.set(item);
    } else {
      const menuId = this._menuId();
      if (menuId) this.cartService.addItem(item, menuId);
    }
  }

  protected changeQty(item: CateringMenuItem, delta: number): void {
    const menuId = this._menuId();
    if (!menuId) return;
    const totalQty = this.cartService.getTotalQuantityForItem(item._id);

    if (delta > 0) {
      if (item.questions?.length) {
        // Always open customise for items with options (each add = new config)
        this.customisingItem.set(item);
      } else {
        this.cartService.addItem(item, menuId);
      }
    } else {
      // Decrement: remove from the last added line for this item
      const lines = this.cartService.getLinesForItem(item._id);
      if (lines.length > 0) {
        const lastLine = lines[lines.length - 1];
        this.cartService.removeItem(lastLine.cartLineId);
      }
    }
  }

  protected onCustomiseConfirmed(event: {
    item: CateringMenuItem;
    quantity: number;
    answers: { questionName: string; answer: string }[];
  }): void {
    const menuId = this._menuId();
    if (!menuId) return;

    // Add the configured item — addItem handles dedup by cartLineId
    for (let i = 0; i < event.quantity; i++) {
      this.cartService.addItem(event.item, menuId, event.answers);
    }
    this.customisingItem.set(null);
  }

  protected onViewCart(): void {
    this.meetingService.navigateTo(BookMeetingView.CART_REVIEW);
  }

  protected switchToEmailRequest(): void {
    this.meetingService.navigateTo(BookMeetingView.EMAIL_REQUEST);
  }
}
